import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Cookie, ShieldCheck } from "lucide-react";

const STORAGE_KEY = "rcps-cookie-consent-v1";

const DEFAULT_PREFS = {
    necessary: true, // toujours actif, non modifiable
    analytics: false,
    marketing: false,
};

function loadConsent() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

function saveConsent(prefs) {
    try {
        localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
                ...prefs,
                necessary: true,
                timestamp: new Date().toISOString(),
                version: 1,
            }),
        );
        // Broadcast event so open dialog / other parts can react
        window.dispatchEvent(new CustomEvent("rcps-consent-change", { detail: prefs }));
    } catch {
        /* ignore */
    }
}

export function openCookiePreferences() {
    window.dispatchEvent(new Event("rcps-open-cookie-prefs"));
}

export default function CookieConsent() {
    const [visible, setVisible] = useState(false);
    const [prefsOpen, setPrefsOpen] = useState(false);
    const [prefs, setPrefs] = useState(DEFAULT_PREFS);

    useEffect(() => {
        const existing = loadConsent();
        if (!existing) {
            setVisible(true);
        } else {
            setPrefs({ ...DEFAULT_PREFS, ...existing });
        }

        const openHandler = () => setPrefsOpen(true);
        window.addEventListener("rcps-open-cookie-prefs", openHandler);
        return () => window.removeEventListener("rcps-open-cookie-prefs", openHandler);
    }, []);

    const acceptAll = useCallback(() => {
        const next = { necessary: true, analytics: true, marketing: true };
        setPrefs(next);
        saveConsent(next);
        setVisible(false);
        setPrefsOpen(false);
    }, []);

    const refuseAll = useCallback(() => {
        const next = { necessary: true, analytics: false, marketing: false };
        setPrefs(next);
        saveConsent(next);
        setVisible(false);
        setPrefsOpen(false);
    }, []);

    const savePreferences = useCallback(() => {
        saveConsent(prefs);
        setVisible(false);
        setPrefsOpen(false);
    }, [prefs]);

    return (
        <>
            {visible && (
                <div
                    role="dialog"
                    aria-live="polite"
                    aria-label="Consentement cookies"
                    data-testid="cookie-banner"
                    className="fixed left-4 right-4 bottom-4 md:left-6 md:right-auto md:bottom-6 md:max-w-md z-[100]"
                >
                    <div className="bg-white border border-concrete-200 shadow-[0_25px_60px_rgba(2,6,23,0.20)] rounded-md overflow-hidden">
                        <div className="px-5 pt-5 pb-4 flex items-start gap-3">
                            <div className="w-9 h-9 rounded-md bg-concrete-100 text-navy-700 flex items-center justify-center flex-shrink-0">
                                <Cookie className="w-4 h-4" />
                            </div>
                            <div>
                                <p className="font-heading text-navy-700 font-semibold text-base leading-tight">
                                    Ce site Web utilise des cookies
                                </p>
                                <p className="mt-2 text-xs text-navy-700/70 leading-relaxed">
                                    Nous utilisons des cookies pour personnaliser le contenu, les publicités et analyser
                                    notre trafic. Nous partageons également des informations sur votre utilisation de
                                    notre site avec nos partenaires de publicité et d’analyse qui peuvent les combiner
                                    avec d’autres informations que vous leur avez fournies ou qu’ils ont collectées
                                    lors de votre utilisation de leurs services.
                                </p>
                            </div>
                        </div>
                        <div className="px-5 pb-5 grid grid-cols-1 gap-2 sm:grid-cols-3">
                            <Button
                                data-testid="cookie-accept-all"
                                onClick={acceptAll}
                                className="h-10 bg-navy-700 hover:bg-navy-800 text-white font-semibold rounded-md text-sm order-first sm:order-last"
                            >
                                Accepter tout
                            </Button>
                            <Button
                                data-testid="cookie-refuse"
                                onClick={refuseAll}
                                variant="outline"
                                className="h-10 rounded-md text-sm border-concrete-300 text-navy-700 hover:bg-concrete-100"
                            >
                                Refuser
                            </Button>
                            <Button
                                data-testid="cookie-personalize"
                                onClick={() => setPrefsOpen(true)}
                                variant="ghost"
                                className="h-10 rounded-md text-sm text-navy-700 hover:bg-concrete-100"
                            >
                                Personnaliser
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
                <DialogContent data-testid="cookie-preferences" className="max-w-lg">
                    <DialogHeader>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-md bg-concrete-100 text-navy-700 flex items-center justify-center">
                                <ShieldCheck className="w-5 h-5" />
                            </div>
                            <div>
                                <DialogTitle className="font-heading text-navy-700 text-xl tracking-tight">
                                    Centre de préférences cookies
                                </DialogTitle>
                                <DialogDescription className="text-navy-700/70 mt-1 text-xs">
                                    Gérez précisément les catégories de cookies utilisées sur ce site.
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="mt-2 space-y-3">
                        <PrefRow
                            title="Cookies strictement nécessaires"
                            desc="Indispensables au bon fonctionnement du site (navigation, formulaires, sécurité). Ils ne peuvent pas être désactivés."
                            checked={true}
                            disabled
                        />
                        <PrefRow
                            title="Cookies de mesure d’audience"
                            desc="Nous aident à comprendre comment les visiteurs utilisent le site (statistiques anonymisées)."
                            checked={prefs.analytics}
                            onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                            testId="pref-analytics"
                        />
                        <PrefRow
                            title="Cookies marketing & publicité"
                            desc="Utilisés pour proposer des contenus et des publicités adaptés à vos centres d’intérêt."
                            checked={prefs.marketing}
                            onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                            testId="pref-marketing"
                        />
                    </div>

                    <DialogFooter className="mt-4 gap-2 sm:gap-2">
                        <Button
                            onClick={refuseAll}
                            variant="outline"
                            className="h-10 rounded-md text-sm border-concrete-300 text-navy-700 hover:bg-concrete-100"
                        >
                            Tout refuser
                        </Button>
                        <Button
                            onClick={savePreferences}
                            data-testid="cookie-save-prefs"
                            className="h-10 rounded-md text-sm bg-navy-700 hover:bg-navy-800 text-white font-semibold"
                        >
                            Enregistrer mes préférences
                        </Button>
                        <Button
                            onClick={acceptAll}
                            className="h-10 rounded-md text-sm bg-white border border-navy-700 text-navy-700 hover:bg-concrete-100 font-semibold"
                        >
                            Tout accepter
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function PrefRow({ title, desc, checked, onChange, disabled = false, testId }) {
    return (
        <div className="border border-concrete-200 rounded-md p-4 flex items-start gap-4">
            <div className="flex-1">
                <p className="font-heading text-navy-700 font-semibold text-sm">{title}</p>
                <p className="mt-1 text-xs text-navy-700/70 leading-relaxed">{desc}</p>
            </div>
            <div className="pt-0.5">
                <Switch
                    checked={!!checked}
                    disabled={disabled}
                    onCheckedChange={onChange}
                    data-testid={testId}
                />
            </div>
        </div>
    );
}
